import _ from 'lodash';
import {get as _get} from 'lodash';
import initialState from '../initialstate';
import * as types from '../../actions/actiontypes';
import {HttpStatusCodes, entityAdmin, projectOwner, userContact, UserType} from '../../util/uiconstants';
import updateKeyContactsCount from './updateKeyContactsCount';
import Utility from '../../util/utilityfunctions';

const sortCustomColumns = (data) => Utility.sortListData(data, 'name');

export default function projectDetailsReducer(state = initialState.project, action) {
	switch (action.type) {
		case types.SET_CUSTOM_TAGS: {
			const status = _get(action, 'responseObj.status', null);
			if (status === HttpStatusCodes.NoContent) {
				const standardTags = [...state.tags.filter((t) => !t.isCustom)];
				const customTags = _get(action, 'tagsData', []);

				return {...state, tags: [...standardTags, ...customTags]};
			}
			return state;
		}
		case types.GET_PROJECT_DETAILS: {
			const customColumns = [...action.response.customColumns]?.map((c) => {
				return {
					...c,
					apiId: c.id,
					options: [...c?.options]?.map((o) => {
						return {
							...o,
							apiId: o.id
						};
					})
				};
			});

			let result = {...action.response, customColumns: sortCustomColumns(customColumns)};

			if (_.size(action.response) > 0 && action.payload && action.payload.transform) {
				let orgStructure = action.response.orgStructure.map((e) => {
					let parentEntity = action.response.orgStructure.find((o) => {
						if (e.parentId !== null && e.parentId === o.id) return o;
					});
					return {
						...e,
						countAccessGroups: e.accessGroups.length,
						countEngagements: e.engagements.length,
						parentEntityName: parentEntity ? parentEntity.entityName : null
					};
				});
				let engagements = action.response.engagementsDetails.map((e) => {
					return {
						...e,
						connectedEntityName: e.connectedEntities[0]?.entityName
					};
				});
				result = {
					...action.response,
					orgStructure: orgStructure,
					engagementsDetails: engagements
				};
			}
			return result;
		}
		case types.UPDATE_PROJECT_STATUS: {
			if (typeof action.response === 'string') {
				const updatedStatus = action.response;
				let currentState = _.cloneDeep(state);
				currentState.status = updatedStatus;
				return currentState;
			} else {
				return state;
			}
		}
		case types.CLEAN_PROJECT_DETAILS: {
			return initialState.project;
		}
		case types.UPDATE_PROJECT_CUSTOM_COLUMNS: {
			const status = _get(action, 'responseObj.status', null);
			if (status === HttpStatusCodes.Ok) {
				const data = _get(action, 'response', []);
				let currentState = _.cloneDeep(state);
				currentState.customColumns = sortCustomColumns(data);
				return currentState;
			}
			return state;
		}
		case types.UPDATE_DISPLAY_DATA: {
			const entities = _get(action, 'entities', null);
			const body = _get(action, 'body', null);
			if (entities !== null && entities.some(({contacts}) => contacts?.length > 0)) {
				//this is to update the Project Owner List, after adding a new Project Owner with MAForm
				let updatedUsers = state.users;

				let updatedOrgStructure = _.cloneDeep(state.orgStructure);

				if (body) {
					// If userLevel access is project owner and operation is true and the entity is 0, then add the users as owners
					// If userLevel access is project owner and operation is false, do nothing
					if (
						body?.entityId == entities[0].entityId &&
						body?.entityOperation &&
						body?.userLevelAccess === projectOwner
					) {
						const entityContacts = entities[0].contacts;
						updatedUsers = state.users
							.map((user) => {
								const contact = entityContacts.find(
									(contact) => contact.userId === user.userId && contact.isProjectOwner
								);
								if (contact) {
									return {...user, isProjectOwner: true};
								}
								return user;
							})
							.concat(
								entityContacts.filter((contact) => {
									if (contact?.isProjectOwner) {
										const userExists = state.users.some((user) => user.userId === contact.userId);
										return !userExists;
									}
									return false;
								})
							);
					} else if (
						body?.userLevelAccess === projectOwner &&
						(body?.entityOperation === undefined || !body?.entityOperation)
					) {
						updatedOrgStructure = updatedOrgStructure?.map((orgElement) => {
							if (orgElement.entityId === body?.entityId) {
								const updatedEntityAdmins = orgElement.entityAdmins.filter(
									(contact) => !body.contactIds.includes(contact.userId)
								);
								return {
									...orgElement,
									entityAdmins: updatedEntityAdmins
								};
							}
							return orgElement;
						});
					} else if (
						body?.entityId === entities[0].entityId &&
						body?.entityOperation === false &&
						(body?.userLevelAccess === userContact || body?.userLevelAccess === entityAdmin)
					) {
						// If userLevel is userContact and operation is false, and it is entity root, then remove the users as owner
						updatedUsers = state.users.map((user) => {
							if (body.contactIds.includes(user.userId)) {
								return {...user, isProjectOwner: false};
							}
							return user;
						});
					}

					//this will update entity admins in the org structure, after adding a new entity admin with MAForm
					if (body?.userLevelAccess === entityAdmin && (body?.entityOperation === undefined || body?.entityOperation)) {
						updatedOrgStructure = updatedOrgStructure?.map((orgElement) => {
							const matchingEntity = entities.find((entity) => entity.entityId === orgElement.entityId);

							if (matchingEntity) {
								const entityAdmins = matchingEntity.contacts
									.filter((contact) => contact.isEntityAdmin)
									.map((contact) => ({
										userId: contact.userId,
										firstName: contact.name,
										lastName: contact.lastName,
										userType: contact.isInternal ? UserType.Member : UserType.Guest,
										emailAddress: contact.emailAddress
									}));

								return {
									...orgElement,
									entityAdmins: [...orgElement.entityAdmins, ...entityAdmins]
								};
							}
							return orgElement;
						});
					} else if (body?.userLevelAccess === userContact && body?.entityOperation === undefined) {
						updatedOrgStructure = updatedOrgStructure?.map((orgElement) => {
							if (orgElement.entityId === body?.entityId) {
								const updatedEntityAdmins = orgElement.entityAdmins.filter(
									(contact) => !body.contactIds.includes(contact.userId)
								);
								return {
									...orgElement,
									entityAdmins: updatedEntityAdmins
								};
							}
							return orgElement;
						});
					}
					return {
						...state,
						keyContactsCount: updateKeyContactsCount(entities),
						users: updatedUsers,
						orgStructure: updatedOrgStructure
					};
				}
				return state;
			}
		}
		case types.ADD_POTENTIAL_CONTACT: {
			const status = _get(action, 'responseObj.status', null);

			if (status === HttpStatusCodes.Created) {
				if (action?.contact?.isInternal === false) {
					const newOrgStructure = state?.orgStructure?.map((entity) => {
						if (entity?.entityId === action?.entityId) {
							let newEntity = _.cloneDeep(entity);
							const userDomain = `@${action?.contact?.emailAddress?.split('@')[1]}`;

							if (!entity?.domains?.includes(userDomain)) {
								newEntity?.domains?.push(userDomain);
							}

							return newEntity;
						}

						return entity;
					});

					return {...state, orgStructure: [...newOrgStructure]};
				}
			}

			return state;
		}
		case types.UPDATE_ENTITY: {
			if (action?.responseObj?.status === HttpStatusCodes.Ok && action?.meta?.api?.data?.stepUpdated === 99) {
				const updatedOrgchart = state.orgStructure?.map((node) => {
					if (node.entityId === action?.meta?.entity?.entityId) {
						const newNode = {...node};
						newNode.label = action?.meta?.entity?.entityName;
						newNode.domains = action?.meta?.entity?.domains;
						return newNode;
					}
					return node;
				});

				return {
					...state,
					orgStructure: updatedOrgchart
				};
			}
			return state;
		}
		default:
			return state;
	}
}
